import Phaser from "phaser";

export default class RoomScene extends Phaser.Scene {
  constructor() {
    super("RoomScene");
  }

  // ---------- RESET STATE ----------
  init() {
    this.currentClueIndex = 0;
    this.items = [];
    this.modal = null;
    this.startTime = Date.now();
    this.score = 1000;
    this.authPassed = false;
    this.failedAttempts = 0;
    this.gameStarted = false;
  }

  create() {
    const { width, height } = this.scale;
    const hints = [
      {
        text: "Inspect logs for suspicious activity",
        correctKey: "log",
        success: "Brute-force attack detected and blocked.",
        explain:
          "Repeated failed logins from a single IP indicate brute-force attempts.",
      },
      {
        text: "Identify the vulnerable server",
        correctKey: "server",
        success: "Critical vulnerability patched.",
        explain: "Unpatched servers are a common entry point for attackers.",
      },
      {
        text: "Locate the exposed API key",
        correctKey: "key",
        success: "API key rotated and secured.",
        explain: "Exposed secrets allow unauthorized access to services.",
      },
      {
        text: "Verify strong authentication policy",
        correctKey: "terminal",
        success: "Password policy enforced.",
        explain: "Strong passwords reduce credential-based attacks.",
      },
      {
        text: "Apply Zero Trust to unlock exit",
        correctKey: "door",
        success: "Zero Trust validated.",
        explain: "Zero Trust requires authentication before access.",
      },
    ];
    // ---------- PASSWORD INPUT ----------
    this.passwordInput = document.createElement("input");
    this.passwordInput.type = "password";
    this.passwordInput.placeholder = "Enter password";
    Object.assign(this.passwordInput.style, {
      position: "absolute",
      display: "none",
      zIndex: 1000,
      padding: "10px",
      fontSize: "16px",
    });
    document.body.appendChild(this.passwordInput);

    this.events.on("shutdown", () => {
      document.body.removeChild(this.passwordInput);
    });
    const iconSize = 80;
    const margin = 20;
    // ---------- BACKGROUND ----------
    this.add.image(width / 2, height / 2, "room").setDisplaySize(width, height);
    const icon = this.add
      .image(width - iconSize / 2 - margin, iconSize / 2 + margin, "vnc")
      .setDisplaySize(iconSize, iconSize)
      .setDepth(10)
      .setInteractive({ useHandCursor: true });

    icon.on("pointerdown", () => {
      this.openModal(
        "🖥 Live VNC Session",
        "Inspect the compromised system below:",
        "http://192.168.3.176:3000/vnc_console?title=Workstation%20Name&url=http://192.168.3.113:6080/vnc_lite.html?path=%3Ftoken%3D0191cb7e-ba5a-416d-a224-379bf8a9fdce",
      );
    });

    // ---------- TIMER ----------
    this.timerText = this.add.text(20, 20, "⏱ Time: 0s", {
      fontSize: "16px",
      fill: "#00e676",
    });

    // ---------- CLUES ----------
    this.clues = hints;

    // ---------- CLUE TEXT ----------
    this.clueText = this.add
      .text(width / 2, 40, "", {
        fontSize: "20px",
        fill: "#00e5ff",
        fontStyle: "bold",
      })
      .setOrigin(0.5);

    this.animateClueText();
    this.updateClueText();

    // ---------- OBJECTS ----------
    this.objectsData = [
      { key: "log" },
      { key: "server" },
      { key: "key" },
      { key: "terminal" },
      { key: "door" },
    ];

    this.authPassword = "ZeroTrust123";

    this.spawnObjects();
    this.gameStarted = false;

    this.openWelcomeModal();
  }

  // ---------- UPDATE ----------
  update() {
    const elapsed = Math.floor((Date.now() - this.startTime) / 1000);
    this.timerText.setText(`⏱ Time: ${elapsed}s`);
  }

  // ---------- CLUE TEXT ----------
  animateClueText() {
    this.tweens.add({
      targets: this.clueText,
      alpha: { from: 0.7, to: 1 },
      duration: 900,
      yoyo: true,
      repeat: -1,
    });
  }

  updateClueText() {
    if (!this.clues[this.currentClueIndex]) return;
    this.clueText.setText(
      `🛡 Task ${this.currentClueIndex + 1}: ${
        this.clues[this.currentClueIndex].text
      }`,
    );
  }
  openWelcomeModal() {
    if (this.modal) return;

    const { width, height } = this.scale;
    const cx = width / 2;
    const cy = height / 2;

    const overlay = this.add
      .rectangle(cx, cy, width, height, 0x000000, 0.75)
      .setInteractive();

    const box = this.add.rectangle(cx, cy, 520, 320, 0xffffff);

    const title = this.add
      .text(cx, cy - 120, "🛡 Cyber Range Briefing", {
        fontSize: "22px",
        color: "#000",
        fontStyle: "bold",
      })
      .setOrigin(0.5);

    const message = this.add
      .text(
        cx,
        cy - 20,
        `Welcome Analyst 👨‍💻

You are entering a compromised SOC lab.

Your mission:
• Investigate security alerts
• Patch vulnerabilities
• Secure exposed credentials
• Enforce Zero Trust

Complete all tasks to escape.`,
        {
          fontSize: "15px",
          color: "#333",
          align: "center",
          wordWrap: { width: 460 },
        },
      )
      .setOrigin(0.5);

    const enterBtn = this.add
      .text(cx - 100, cy + 110, "🚀 ENTER LAB", {
        fontSize: "16px",
        backgroundColor: "#0288d1",
        padding: { x: 18, y: 10 },
        color: "#fff",
      })
      .setOrigin(0.5)
      .setInteractive();

    const exitBtn = this.add
      .text(cx + 100, cy + 110, "🚪 EXIT", {
        fontSize: "16px",
        backgroundColor: "#c62828",
        padding: { x: 18, y: 10 },
        color: "#fff",
      })
      .setOrigin(0.5)
      .setInteractive();

    enterBtn.on("pointerdown", () => {
      this.gameStarted = true;
      this.startTime = Date.now(); // reset timer on entry
      this.modal.destroy(true);
      this.modal = null;
    });

    exitBtn.on("pointerdown", () => {
      this.scene.stop(); // or route away in React
    });

    this.modal = this.add.container(0, 0, [
      overlay,
      box,
      title,
      message,
      enterBtn,
      exitBtn,
    ]);
  }
  // ---------- OBJECT SPAWN ----------
  spawnObjects() {
    const { width, height } = this.scale;
    const padding = 150;
    this.objectsData = [
      { key: "log" },
      { key: "server" },
      { key: "key" },
      { key: "terminal" },
      { key: "door" },
    ];
    this.items.forEach((i) => i.destroy());
    this.items = [];

    this.objectsData.forEach((obj) => {
      const x = Phaser.Math.Between(padding, width - padding);
      const y = Phaser.Math.Between(padding + 80, height - padding);

      const item = this.add
        .image(x, y, obj.key)
        .setDisplaySize(180, 160)
        .setInteractive({ useHandCursor: true });

      item.on("pointerover", () => item.setTint(0x00e5ff));
      item.on("pointerout", () => item.clearTint());
      item.on("pointerdown", () => this.handleItemClick(obj.key));

      this.items.push(item);
    });
  }

  // ---------- CLICK HANDLER ----------
  handleItemClick(key) {
    if (!this.gameStarted) return;
    if (this.modal) return;
    if (this.currentClueIndex >= this.clues.length) return;

    const clue = this.clues[this.currentClueIndex];

    if (key === "door" && !this.authPassed) {
      this.openPasswordModal();
      return;
    }

    if (key === clue.correctKey) {
      this.currentClueIndex++;

      if (this.currentClueIndex === this.clues.length) {
        this.showGameComplete();
      } else {
        this.updateClueText();
        this.openModal(
          "✔ Action Completed",
          `${clue.success}\n\n🎓 Why this matters:\n${clue.explain}`,
        );
      }
    } else {
      this.score -= 50;
      this.openModal(
        "⚠ Incorrect Action",
        "This action does not resolve the current security issue.",
      );
    }
  }

  // ---------- PASSWORD MODAL ----------
  openPasswordModal() {
    if (this.modal) return;

    const { width, height } = this.scale;
    const cx = width / 2;
    const cy = height / 2;

    const overlay = this.add
      .rectangle(cx, cy, width, height, 0x000000, 0.7)
      .setInteractive();

    const box = this.add.rectangle(cx, cy, 420, 240, 0xffffff);

    const title = this.add
      .text(cx, cy - 80, "🔐 Authentication Required", {
        fontSize: "20px",
        color: "#000",
        fontStyle: "bold",
      })
      .setOrigin(0.5);

    const canvasBounds = this.game.canvas.getBoundingClientRect();
    this.passwordInput.style.left = `${canvasBounds.left + cx - 120}px`;
    this.passwordInput.style.top = `${canvasBounds.top + cy - 10}px`;
    this.passwordInput.style.width = "240px";
    this.passwordInput.style.display = "block";
    this.passwordInput.value = "";
    this.passwordInput.focus();

    const submit = this.add
      .text(cx, cy + 50, "SUBMIT", {
        fontSize: "14px",
        backgroundColor: "#2e7d32",
        padding: { x: 18, y: 8 },
        color: "#fff",
      })
      .setOrigin(0.5)
      .setInteractive();

    submit.on("pointerdown", () => this.validatePassword());

    this.modal = this.add.container(0, 0, [overlay, box, title, submit]);
  }

  validatePassword() {
    const entered = this.passwordInput.value;

    if (entered === this.authPassword) {
      this.passwordInput.style.display = "none";
      this.authPassed = true;

      this.modal.destroy(true);
      this.modal = null;

      this.currentClueIndex++;
      this.showGameComplete();
    } else {
      this.failedAttempts++;
      this.score -= 100;

      this.passwordInput.value = "";

      if (this.failedAttempts >= 3) {
        this.passwordInput.style.display = "none";
        this.openModal("🚨 Account Locked", "Too many failed attempts.");
      } else {
        this.openModal(
          "❌ Authentication Failed",
          "Invalid password. Try again.",
        );
      }
    }
  }

  // ---------- GAME COMPLETE ----------
  showGameComplete() {
    const time = Math.floor((Date.now() - this.startTime) / 1000);

    this.clueText.setText("🎉 SOC LAB SECURED");

    this.openModal(
      "Mission Complete",
      `⏱ Time: ${time}s\n🏆 Score: ${this.score}`,
    );
  }

  // ---------- MODAL ----------
  openModal(title, message, url = "") {
    if (this.modal) return;

    const { width, height } = this.scale;
    const cx = width / 2;
    const cy = height / 2;

    // ---------- Overlay ----------
    const overlay = this.add
      .rectangle(cx, cy, width, height, 0x000000, 0.7)
      .setInteractive();

    const boxWidth = url ? 900 : 440;
    const boxHeight = url ? 520 : 260;

    const box = this.add.rectangle(cx, cy, boxWidth, boxHeight, 0xffffff);

    const titleText = this.add
      .text(cx, cy - boxHeight / 2 + 30, title, {
        fontSize: "18px",
        color: "#000",
        fontStyle: "bold",
      })
      .setOrigin(0.5);

    const msgText = this.add
      .text(cx, cy - boxHeight / 2 + 70, message, {
        fontSize: "14px",
        color: "#333",
        wordWrap: { width: boxWidth - 40 },
        align: "center",
      })
      .setOrigin(0.5);

    // ---------- OPTIONAL IFRAME ----------
    let iframe = null;

    if (url) {
      iframe = document.createElement("iframe");
      iframe.src = url;
      iframe.style.position = "absolute";
      iframe.style.border = "1px solid #ccc";
      iframe.style.borderRadius = "6px";
      iframe.style.background = "#000";
      iframe.style.zIndex = 1000;

      const canvasBounds = this.game.canvas.getBoundingClientRect();

      iframe.style.left = `${canvasBounds.left + cx - boxWidth / 2 + 20}px`;
      iframe.style.top = `${canvasBounds.top + cy - boxHeight / 2 + 100}px`;
      iframe.style.width = `${boxWidth - 40}px`;
      iframe.style.height = `${boxHeight - 160}px`;

      document.body.appendChild(iframe);
    }

    // ---------- Close Button ----------
    const close = this.add
      .text(cx, cy + boxHeight / 2 - 30, "CLOSE", {
        fontSize: "14px",
        backgroundColor: "#2e7d32",
        padding: { x: 18, y: 8 },
        color: "#fff",
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    close.on("pointerdown", () => {
      if (iframe) {
        document.body.removeChild(iframe);
      }
      this.modal.destroy(true);
      this.modal = null;
    });

    // ---------- Store modal ----------
    this.modal = this.add.container(0, 0, [
      overlay,
      box,
      titleText,
      msgText,
      close,
    ]);
  }
}
